import React, { useState, useEffect, useCallback } from 'react';
import {
  Tabs, Table, Button, Input, Modal, Select, InputNumber, Tag, Space, Empty,
  message, Popconfirm, Typography
} from 'antd';
import {
  PlusOutlined, DownloadOutlined, EditOutlined, DeleteOutlined,
  SearchOutlined
} from '@ant-design/icons';
import {
  fetchAvailableProducts,
  fetchHoldTimeStages,
  fetchHoldTimeRules,
  createHoldTimeRule,
  updateHoldTimeRule,
  deleteHoldTimeRule,
} from './api';
import HoldTimeTracker from './HoldTimeTracker';
import WipTrackerComponent from './WipTracker';

const { Title, Text } = Typography;

// ─── Stage badge colors ──────────────────────────────────────────────────────
const STAGE_BADGE_COLORS = {
  Dispensing:   { bg: '#fef3c7', color: '#92400e' },
  Granulation:  { bg: '#dbeafe', color: '#1e40af' },
  Blending:     { bg: '#d1fae5', color: '#065f46' },
  Compression:  { bg: '#ede9fe', color: '#5b21b6' },
  Coating:      { bg: '#fce7f3', color: '#9d174d' },
  Capsulation:  { bg: '#fff0f6', color: '#c41d7f' },
  Imprinting:   { bg: '#f0f5ff', color: '#2f54eb' },
  Packing:      { bg: '#e0f2fe', color: '#0c4a6e' },
};

const StageBadge = ({ stage }) => {
  const c = STAGE_BADGE_COLORS[stage] || { bg: '#f3f4f6', color: '#374151' };
  return (
    <Tag style={{ backgroundColor: c.bg, color: c.color, border: 'none', fontWeight: 600, fontSize: 13 }}>
      {stage}
    </Tag>
  );
};

// ─── Master Rules Tab ─────────────────────────────────────────────────────────

const MasterRules = () => {
  const [rules, setRules] = useState([]);
  const [products, setProducts] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchProduct, setSearchProduct] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  const loadRules = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchProduct) params.product_code = searchProduct;
      const res = await fetchHoldTimeRules(params);
      setRules(res.data || []);
    } catch (err) {
      message.error('Failed to load rules');
    } finally {
      setLoading(false);
    }
  }, [searchProduct]);

  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [prodRes, stageRes] = await Promise.all([
          fetchAvailableProducts(),
          fetchHoldTimeStages(),
        ]);
        setProducts(prodRes.data || []);
        setStages(stageRes.data || stageRes.stages || []);
      } catch (err) {
        message.error('Failed to load dropdown data');
      }
    };
    loadDropdowns();
  }, []);

  useEffect(() => {
    loadRules();
  }, [loadRules]);

  const handleDelete = async (id) => {
    try {
      await deleteHoldTimeRule(id);
      message.success('Rule deleted');
      loadRules();
    } catch {
      message.error('Failed to delete rule');
    }
  };

  const openAdd = () => { setEditingRule(null); setModalOpen(true); };
  const openEdit = (rule) => { setEditingRule(rule); setModalOpen(true); };

  const handleExport = () => {
    if (!rules.length) { message.info('No rules to export'); return; }
    const headers = ['Product Code', 'Product Name', 'From Stage', 'To Stage', 'Limit (Days)', 'Rule Type'];
    const rows = rules.map((r) => [
      r.product_code,
      r.product_name || '',
      r.from_stage,
      r.to_stage,
      r.hold_time_limit_days,
      r.rule_type === 'overarching' ? 'Overarching Cycle Limit' : 'Inter-stage',
    ]);
    const csv = [headers, ...rows].map((r) => r.map((c) => `"${c}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'hold_time_rules.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: 'Product Code',
      dataIndex: 'product_code',
      key: 'product_code',
      width: 140,
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: 'Product Name',
      dataIndex: 'product_name',
      key: 'product_name',
      width: 200,
      render: (v) => v || '—',
    },
    {
      title: 'From Stage',
      dataIndex: 'from_stage',
      key: 'from_stage',
      width: 150,
      render: (v) => <StageBadge stage={v} />,
    },
    {
      title: 'To Stage',
      dataIndex: 'to_stage',
      key: 'to_stage',
      width: 150,
      render: (v) => <StageBadge stage={v} />,
    },
    {
      title: 'Limit (Days)',
      dataIndex: 'hold_time_limit_days',
      key: 'hold_time_limit_days',
      width: 120,
      align: 'center',
      render: (v) => <Text strong style={{ fontSize: 15 }}>{v}</Text>,
    },
    {
      title: 'Rule Type',
      dataIndex: 'rule_type',
      key: 'rule_type',
      width: 200,
      render: (v) => v === 'overarching'
        ? <Text style={{ color: '#dc2626', fontWeight: 600 }}>Overarching Cycle Limit</Text>
        : <Text type="secondary">Inter-stage</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 160,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>Edit</Button>
          <Popconfirm
            title="Delete this rule?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        <Title level={4} style={{ margin: 0 }}>Master Hold Time Rules — Per Product Code</Title>
        <Space>
          <Input
            placeholder="Search by product code..."
            prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
            value={searchProduct}
            onChange={(e) => setSearchProduct(e.target.value.toUpperCase())}
            style={{ width: 240 }}
            allowClear
          />
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
            style={{ backgroundColor: '#22c55e', borderColor: '#22c55e' }}>
            Add Rule
          </Button>
          <Button icon={<DownloadOutlined />} onClick={handleExport}>Export Rules</Button>
        </Space>
      </div>

      {/* Info bar */}
      <div style={{
        background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6,
        padding: '10px 16px', marginBottom: 12, fontSize: 13, color: '#991b1b'
      }}>
        <Text type="danger" strong>i</Text>{' '}
        Formula: <Text strong>End of "To Stage" – End of "From Stage"</Text> (fixed per product code).
        Warning within <Text type="danger" strong>7 days</Text> of limit. Dispensing→Packing is the overarching cycle limit.
      </div>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={rules}
        rowKey="id"
        loading={loading}
        size="middle"
        pagination={{ pageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} rules` }}
        locale={{ emptyText: <Empty description="No rules found" /> }}
        scroll={{ x: 1100 }}
      />

      {/* Modal */}
      {modalOpen && (
        <RuleModal
          rule={editingRule}
          products={products}
          stages={stages}
          onClose={() => setModalOpen(false)}
          onSaved={() => { setModalOpen(false); message.success(editingRule ? 'Rule updated' : 'Rule created'); loadRules(); }}
        />
      )}
    </div>
  );
};

// ─── Add / Edit Modal ─────────────────────────────────────────────────────────

const RuleModal = ({ rule, products, stages, onClose, onSaved }) => {
  const isEdit = !!rule;
  const [form, setForm] = useState({
    product_code: rule?.product_code || undefined,
    from_stage: rule?.from_stage || undefined,
    to_stage: rule?.to_stage || undefined,
    hold_time_limit_days: rule?.hold_time_limit_days || null,
    rule_type: rule?.rule_type || 'interstage',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    if (!form.product_code || !form.from_stage || !form.to_stage || !form.hold_time_limit_days) {
      message.warning('Please fill all required fields');
      return;
    }
    try {
      setSaving(true);
      const payload = {
        product_code: form.product_code,
        from_stage: form.from_stage,
        to_stage: form.to_stage,
        hold_time_limit_days: Number(form.hold_time_limit_days),
        rule_type: form.rule_type,
      };
      if (isEdit) {
        await updateHoldTimeRule(rule.id, payload);
      } else {
        await createHoldTimeRule(payload);
      }
      onSaved();
    } catch {
      message.error('Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const stageOptions = stages.map((s) => {
    const name = typeof s === 'string' ? s : s.stage_name || s.name || s.label;
    return { label: name, value: name };
  });

  return (
    <Modal
      title={isEdit ? 'Edit Hold Time Rule' : 'Add Hold Time Rule'}
      open={true}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={saving}
      okText={isEdit ? 'Update Rule' : 'Add Rule'}
      width={540}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 18, paddingTop: 8 }}>
        {/* Product Code */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Product Code</Text>
          <Select
            showSearch
            placeholder="Select product"
            value={form.product_code}
            onChange={(v) => setForm((f) => ({ ...f, product_code: v }))}
            style={{ width: '100%', marginTop: 4 }}
            options={products.map((p) => ({ label: `${p.value} — ${p.label}`, value: p.value }))}
            filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
          />
        </div>

        {/* From / To Stage */}
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>From Stage</Text>
            <Select
              placeholder="Select stage"
              value={form.from_stage}
              onChange={(v) => setForm((f) => ({ ...f, from_stage: v }))}
              style={{ width: '100%', marginTop: 4 }}
              options={stageOptions}
            />
          </div>
          <div style={{ flex: 1 }}>
            <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>To Stage</Text>
            <Select
              placeholder="Select stage"
              value={form.to_stage}
              onChange={(v) => setForm((f) => ({ ...f, to_stage: v }))}
              style={{ width: '100%', marginTop: 4 }}
              options={stageOptions}
            />
          </div>
        </div>

        {/* Hold Time Limit */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Hold Time Limit (Days)</Text>
          <InputNumber
            min={1}
            placeholder="e.g. 30"
            value={form.hold_time_limit_days}
            onChange={(v) => setForm((f) => ({ ...f, hold_time_limit_days: v }))}
            style={{ width: '100%', marginTop: 4 }}
          />
        </div>

        {/* Rule Type */}
        <div>
          <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5 }}>Rule Type</Text>
          <Select
            value={form.rule_type}
            onChange={(v) => setForm((f) => ({ ...f, rule_type: v }))}
            style={{ width: '100%', marginTop: 4 }}
            options={[
              { label: 'Inter-stage', value: 'interstage' },
              { label: 'Overarching Cycle Limit', value: 'overarching' },
            ]}
          />
        </div>
      </div>
    </Modal>
  );
};

// WipTracker is now imported from ./WipTracker.jsx

// ─── Main BatchTracker Page ────────────────────────────────────────────────────────────

const BatchTracker = () => {
  const tabItems = [
    { key: 'wip-tracker', label: 'WIP Tracker', children: <WipTrackerComponent /> },
    { key: 'hold-time-tracker', label: 'Hold Time Tracker', children: <HoldTimeTracker /> },
    { key: 'master-rules', label: 'Master Rules', children: <MasterRules /> },
  ];

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1600, margin: '0 auto' }}>
      <Title level={3} style={{ marginBottom: 16 }}>Batch Tracker</Title>
      <Tabs defaultActiveKey="wip-tracker" items={tabItems} size="large" />
    </div>
  );
};

export default BatchTracker;
